import React, { useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { CircularProgress } from '@mui/material'

import { getTrafficFetcherTimeout, MAX_FILE_SIZE_MB } from '~/utilities/constants'
import TicketUploadCard from './TicketUploadCard'

const NAVY = '#12275c'
const NAVY_SOFT = '#1b3a7a'
const BORDER = '#e4e9f2'
const MUTED = '#6b7a90'

const SEARCH_STEPS = [
  'Initiating system search...',
  'Cross-referencing records',
  'Validating information',
]

const Page = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 20px 48px;
  ${(props) => props.theme.media.mobile`
    padding: 16px 16px 32px;
  `};
`

const Column = styled.div`
  width: 100%;
  max-width: 570px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const ProgressCard = styled.div`
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${BORDER};
  border-radius: 14px;
  padding: 28px 24px;
  text-align: center;
`

const Title = styled.h1`
  font-family: ${(props) => props.theme.fonts.avenirNextBold};
  color: ${NAVY_SOFT};
  font-size: 1.35rem;
  line-height: 1.3;
  margin: 14px 0 0;
`

const Description = styled.p`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${MUTED};
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 10px 0 0;
`

const ProgressTrack = styled.div`
  margin-top: 22px;
  height: 8px;
  border-radius: 999px;
  background: #d9f3f1;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.$pct}%;
  background: ${NAVY};
  border-radius: 999px;
  transition: width 400ms ease;
`

const StepList = styled.div`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid ${(props) => (props.$active ? NAVY : BORDER)};
  border-radius: 10px;
  padding: 12px 14px;
  text-align: left;
  opacity: ${(props) => (props.$done || props.$active ? 1 : 0.55)};
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const StepSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${BORDER};
  border-top-color: ${NAVY};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`

const StepDot = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${BORDER};
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => (props.$done ? NAVY : 'transparent')};
`

const StepLabel = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => (props.$active ? NAVY : MUTED)};
  font-size: 0.92rem;
`

const formatDurationCopy = (fetcher, isBatched) => {
  const durationSeconds = getTrafficFetcherTimeout(fetcher, isBatched) / 1000
  if (durationSeconds > 60) {
    const minutes = Math.floor(durationSeconds / 60)
    const unit = minutes === 1 ? 'minute' : 'minutes'
    return { text: `${minutes} ${unit}`, ms: durationSeconds * 1000 }
  }
  return { text: `${durationSeconds} seconds`, ms: durationSeconds * 1000 }
}

const TicketLoader = ({ fetcher, isBatched }) => {
  const { text: durationText, ms: timeoutMs } = useMemo(
    () => formatDurationCopy(fetcher, isBatched),
    [fetcher, isBatched]
  )

  const [activeStep, setActiveStep] = useState(0)
  const [progressPct, setProgressPct] = useState(4)

  useEffect(() => {
    const startedAt = Date.now()
    const stepInterval = timeoutMs / SEARCH_STEPS.length

    const tick = () => {
      const elapsed = Date.now() - startedAt
      const pct = Math.min(92, 4 + (elapsed / timeoutMs) * 88)
      setProgressPct(pct)
      setActiveStep(Math.min(SEARCH_STEPS.length - 1, Math.floor(elapsed / stepInterval)))
    }

    tick()
    const id = setInterval(tick, 400)
    return () => clearInterval(id)
  }, [timeoutMs])

  return (
    <Page>
      <Column>
        <ProgressCard>
          <CircularProgress size={36} thickness={4} sx={{ color: NAVY }} />
          <Title>Searching records for you</Title>
          <Description>
            Checking traffic databases. This can take up to {durationText} — please keep this page
            open.
          </Description>
          <ProgressTrack>
            <ProgressFill $pct={progressPct} />
          </ProgressTrack>
          <StepList>
            {SEARCH_STEPS.map((label, index) => {
              const active = index === activeStep
              const done = index < activeStep
              return (
                <StepRow key={label} $active={active} $done={done}>
                  {active ? <StepSpinner /> : <StepDot $done={done} />}
                  <StepLabel $active={active || done}>{label}</StepLabel>
                </StepRow>
              )
            })}
          </StepList>
        </ProgressCard>

        <TicketUploadCard
          subtitle={`Photo, PDF, or scan — up to ${MAX_FILE_SIZE_MB} MB. We analyze it in seconds and tell you if you're likely to save.`}
        />
      </Column>
    </Page>
  )
}

export default TicketLoader
